import usePrevious from 'react-utils/hooks/usePrevious';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getModuleUndoCount, getModuleUndoRedoCount } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getIsDataAuthoringModeActive } from 'ContentEditorUI/redux/selectors/instanceEditingSelectors';
import { selectInstanceEditingUndoCount, selectInstanceEditingUndoRedoCount } from 'structured-content-lib/redux/selectors/instanceEditSelectors';
import { addInlineEditor, removeInlineEditor } from './inlineEditingUtils';
export function useInlineFieldEditingHoverStyles(node) {
  useEffect(() => {
    const onHover = () => {
      node.classList.add('inpage-inline-field-overlay');
    };
    const onLeave = () => {
      node.classList.remove('inpage-inline-field-overlay');
    };
    node.addEventListener('mouseover', onHover);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.classList.remove('inpage-inline-field-overlay');
      node.removeEventListener('mouseover', onHover);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, [node]);
}
export function useNoOutlineWhenFocused(node) {
  useEffect(() => {
    node.classList.add('inpage-inline-field-outline-none');
    return () => {
      node.classList.remove('inpage-inline-field-outline-none');
    };
  }, [node]);
}
export function useWasUndoRedoEvent() {
  const isDataAuthoringModeActive = useSelector(getIsDataAuthoringModeActive);
  const moduleUndoCount = useSelector(getModuleUndoCount);
  const moduleUndoRedoCount = useSelector(getModuleUndoRedoCount);
  const prevModuleUndoCount = usePrevious(moduleUndoCount);
  const prevModuleUndoRedoCount = usePrevious(moduleUndoRedoCount);
  const instanceEditingUndoCount = useSelector(selectInstanceEditingUndoCount);
  const instanceEditingUndoRedoCount = useSelector(selectInstanceEditingUndoRedoCount);
  const prevInstanceEditingUndoCount = usePrevious(instanceEditingUndoCount);
  const prevInstanceEditingUndoRedoCount = usePrevious(instanceEditingUndoRedoCount);
  if (isDataAuthoringModeActive) {
    return prevInstanceEditingUndoCount !== undefined && prevInstanceEditingUndoRedoCount !== undefined && (instanceEditingUndoCount !== prevInstanceEditingUndoCount || instanceEditingUndoRedoCount !== prevInstanceEditingUndoRedoCount);
  }
  return prevModuleUndoCount !== undefined && prevModuleUndoRedoCount !== undefined && (moduleUndoCount !== prevModuleUndoCount || moduleUndoRedoCount !== prevModuleUndoRedoCount);
}
export function useWasUndoRedoEventWithData(data) {
  const prevData = usePrevious(data);
  const wasUndoRedo = useWasUndoRedoEvent();
  return wasUndoRedo && prevData !== undefined && prevData !== data;
}
export function useRegisterInlineFieldEditor(moduleName) {
  useEffect(() => {
    addInlineEditor(moduleName);
    return () => {
      removeInlineEditor(moduleName);
    };
  }, [moduleName]);
}

/**
 *
 * @param moduleName unique name for module
 * @param fieldKeyString period separated key string for accessing the current field (i.e group1.headings.text)
 * @param fieldName name for the current field
 * @returns a group key used for creating undo/redo slices. In data authoring mode, we want to use
 * the field name since a) that will map straight back to the instance property name and b) we want to group updates
 * to the same property, even if those updates occurred in different places. Otherwise, it will create a key
 * that's unique based on the name of the module and the path to the field.
 */
export function useGroupKey(moduleName, fieldKeyString, fieldName) {
  const isDataAuthoringModeActive = useSelector(getIsDataAuthoringModeActive);
  return useMemo(() => isDataAuthoringModeActive ? fieldName : `${moduleName}-${fieldKeyString}`, [fieldKeyString, fieldName, isDataAuthoringModeActive, moduleName]);
}
export function useJoinKeyPathToString(keyPath) {
  return useMemo(() => {
    return keyPath.join('.');
  }, [keyPath]);
}